/**
 * Links to where AI is set up in the dashboard. AI providers and an app's
 * automatic tasks live there (docs/product/ai-capacity/PROVIDERS-AND-MODELS.md);
 * the Agent links to them.
 */

/** The dashboard's App → AI page, next to the app dashboard the Agent already links to. */
export function appAiDashboardUrl(appsUrl: string, appId: string): string {
  return `${appsUrl.replace(/\/+$/, "")}/${encodeURIComponent(appId)}/ai`;
}

/** The dashboard's personal AI accounts page, on the same origin as the app dashboard. */
export function personalAiAccountsUrl(appsUrl: string): string {
  try {
    return new URL("/integrations/ai", appsUrl).toString();
  } catch {
    return "/integrations/ai";
  }
}

/** The dashboard's organisation page, where admins manage the organisation's AI accounts. */
export function organisationAiAccountsUrl(appsUrl: string): string {
  try {
    return new URL("/organisations", appsUrl).toString();
  } catch {
    return "/organisations";
  }
}

/**
 * The dashboard's AI providers page (Organisation → AI providers, or Personal
 * → Integrations → AI on personal installations), or its Connect a provider
 * subpage. Without a known organisation it opens the organisation page.
 */
export function aiProvidersUrl(input: {
  appsUrl: string;
  personal: boolean;
  organisationId: string | null;
  connect?: boolean;
}): string {
  const page = input.personal
    ? "/integrations/ai"
    : input.organisationId ? `/organisations/${encodeURIComponent(input.organisationId)}/ai` : null;
  const path = page ? `${page}${input.connect ? "/connect" : ""}` : "/organisations";
  try {
    return new URL(path, input.appsUrl).toString();
  } catch {
    return path;
  }
}

const DEFAULT_ACCOUNT_NAMES: Readonly<Record<string, string>> = {
  "Sign in with ChatGPT": "ChatGPT",
  "Sign in with Claude": "Claude",
  "Use an OpenAI API key": "OpenAI API key",
  "Use an Anthropic API key": "Anthropic API key",
};

/** Accounts created with a sign-in method's label read as the provider's name. */
export function aiAccountName(label: string): string {
  return DEFAULT_ACCOUNT_NAMES[label.trim()] ?? label;
}
